import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Calendar as CalIcon, Video, Trash2, Users, Pencil, Clock, BookOpen, PlayCircle, User, Bell } from "lucide-react";
import { toast } from "sonner";

export default function CalendarioPage() {
  const { user } = useAuth();
  const [lezioni, setLezioni] = useState([]);
  const [classi, setClassi] = useState([]);
  const [corsi, setCorsi] = useState([]);
  const [materieAnag, setMaterieAnag] = useState([]);
  const [open, setOpen] = useState(false);
  const [maxEdit, setMaxEdit] = useState(null);
  const [maxVal, setMaxVal] = useState("");
  const [cap, setCap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filtroData, setFiltroData] = useState("");
  const [rec, setRec] = useState({});
  const [notifLez, setNotifLez] = useState(null);
  const [notifRows, setNotifRows] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const emptyForm = { classe_id:"", titolo:"", data:"", ora_inizio:"09:00", ora_fine:"13:00", materia:"", ore_lezione:0, docente_id:"", modalita:"fad_sincrona", max_partecipanti:"" };
  const [form, setForm] = useState(emptyForm);
  const [filtroDocente, setFiltroDocente] = useState("tutti");
  const [filtroMateria, setFiltroMateria] = useState("tutte");
  const [filtroClasse, setFiltroClasse] = useState("tutte");
  const canEdit = user?.role === "admin" || user?.role === "docente" || user?.role === "collaboratore";

  const apriLezione = async (l) => {
    try {
      const { data } = await api.get(`/lezioni/${l.id}/link-accesso`);
      if (data.url) {
        localStorage.setItem("iste_bbb_open_until", String(Date.now() + 3*3600*1000));
        window.open(data.url, "_blank");
      }
      else toast.error("Nessun link disponibile per questa lezione");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore apertura lezione"); }
  };
  const apriRegistrazione = async (l) => {
    try {
      const { data } = await api.get(`/lezioni/${l.id}/registrazione`);
      if (data.url) window.open(data.url, "_blank");
      else toast.info("Registrazione non ancora disponibile");
    } catch { toast.error("Errore registrazione"); }
  };
  const openNotif = async (l) => {
    setNotifLez(l); setNotifRows([]); setNotifLoading(true);
    try { const { data } = await api.get(`/lezioni/${l.id}/notifiche`); setNotifRows(data); }
    catch { toast.error("Errore caricamento notifiche inviate"); }
    finally { setNotifLoading(false); }
  };

  const load = async () => {
    try {
      const [l, c, cs, ma] = await Promise.all([api.get("/lezioni"), api.get("/classi"), api.get("/corsi"), api.get("/materie")]);
      setLezioni(l.data); setClassi(c.data); setCorsi(cs.data); setMaterieAnag(ma.data);
    } catch { toast.error("Errore caricamento calendario"); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const isStaff = user?.role === "admin" || user?.role === "collaboratore" || user?.role === "docente";
  const isManager = user?.role === "admin" || user?.role === "collaboratore";
  const loadCap = async () => {
    if (!isManager) return;
    try { const { data } = await api.get("/bbb/capacita"); setCap(data); } catch (e) { console.error("Errore capacità BBB:", e); }
  };
  useEffect(() => { loadCap(); const t = setInterval(loadCap, 30000); return () => clearInterval(t); }, []);

  const isLiveWindow = (l) => {
    if (!l.data || !l.ora_inizio || !l.ora_fine) return false;
    const start = new Date(`${l.data}T${l.ora_inizio}:00`);
    let end = new Date(`${l.data}T${l.ora_fine}:00`);
    if (end <= start) end = new Date(end.getTime() + 24*3600*1000);
    const now = new Date();
    return now >= new Date(start.getTime() - 15*60000) && now <= new Date(end.getTime() + 45*60000);
  };

  // La lezione è terminata: la registrazione può essere disponibile (dopo l'elaborazione BBB).
  const isEnded = (l) => {
    if (!l.data) return false;
    if (!l.ora_fine) return l.data < new Date().toISOString().slice(0,10);
    const start = new Date(`${l.data}T${l.ora_inizio || l.ora_fine}:00`);
    let end = new Date(`${l.data}T${l.ora_fine}:00`);
    if (end <= start) end = new Date(end.getTime() + 24*3600*1000);
    return new Date() > end;
  };

  // Verifica reale su BigBlueButton: mostra "Guarda registrazione" solo se la registrazione esiste.
  useEffect(() => {
    lezioni.filter(l => l.bbb_link && isEnded(l)).forEach(async (l) => {
      setRec(prev => {
        if (l.id in prev) return prev;
        api.get(`/lezioni/${l.id}/registrazione`)
          .then(({ data }) => setRec(p => ({ ...p, [l.id]: !!data.url })))
          .catch(() => setRec(p => ({ ...p, [l.id]: false })));
        return { ...prev, [l.id]: prev[l.id] };
      });
    });
  }, [lezioni]);

  const save = async () => {
    if (form.modalita === "blended" && !form.docente_id) { toast.error("Seleziona il docente per la lezione blended"); return; }
    if (saving) return;
    if (form.modalita !== "blended" && (!form.ora_inizio || !form.ora_fine)) { toast.error("Inserisci ora inizio e fine"); return; }
    setSaving(true);
    try {
      await api.post("/lezioni", { ...form, titolo: form.materia || "Lezione", ore_lezione: +form.ore_lezione || 0, max_partecipanti: form.max_partecipanti ? +form.max_partecipanti : null });
      toast.success(form.modalita === "blended" ? "Lezione blended creata — stanza BBB generata" : "Lezione online creata — stanza BBB generata automaticamente");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare la lezione?")) return;
    await api.delete(`/lezioni/${id}`); toast.success("Eliminata"); load();
  };

  const openMaxEdit = (l) => { setMaxEdit(l); setMaxVal(l.max_partecipanti ?? ""); };
  const saveMax = async () => {
    try {
      await api.patch(`/lezioni/${maxEdit.id}/max-partecipanti`, { max_partecipanti: maxVal === "" ? null : +maxVal });
      toast.success("Max partecipanti aggiornato");
      setMaxEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };

  const classeSel = classi.find(x => x.id === form.classe_id);
  const isDocente = user?.role === "docente";
  const canBlended = user?.role === "admin" || user?.role === "collaboratore";
  const materieOpzioni = isDocente
    ? materieAnag.filter(m => (m.docenti_ids||[]).includes(user?.id))
    : (user?.role === "collaboratore"
        ? materieAnag
        : (form.docente_id ? materieAnag.filter(m => (m.docenti_ids||[]).includes(form.docente_id)) : materieAnag));

  const docentiDisponibili = [...new Map(classi.flatMap(c=>c.docenti_dettagli||[]).map(d=>[d.id,d])).values()];
  const materieInUso = new Set(lezioni.map(l => l.materia).filter(Boolean));
  const materieDisponibili = materieAnag
    .filter(m => materieInUso.has(m.nome) && (!isDocente || (m.docenti_ids||[]).includes(user?.id)))
    .map(m => m.nome);
  const lezioniFiltrate = lezioni.filter(l =>
    (filtroClasse === "tutte" || l.classe_id === filtroClasse) &&
    (filtroDocente === "tutti" || l.docente_id === filtroDocente) &&
    (filtroData === "" || l.data === filtroData) &&
    (filtroMateria === "tutte" || l.materia === filtroMateria));
  const grouped = lezioniFiltrate.reduce((acc,l)=>{ (acc[l.data]=acc[l.data]||[]).push(l); return acc; }, {});
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (b.ora_inizio || "").localeCompare(a.ora_inizio || "")));
  const dates = Object.keys(grouped).sort().reverse();

  const oggi = new Date().toISOString().slice(0, 10);
  const isLive = (l) => {
    if (!l.data || !l.ora_inizio || !l.ora_fine) return false;
    const s = new Date(`${l.data}T${l.ora_inizio}:00`);
    let e = new Date(`${l.data}T${l.ora_fine}:00`);
    if (e <= s) e = new Date(e.getTime() + 864e5);
    const now = new Date();
    return now >= new Date(s.getTime() - 15 * 60000) && now <= new Date(e.getTime() + 45 * 60000);
  };
  const lezioniOggi = lezioni.filter(l => l.data === oggi).length;
  const liveCount = lezioni.filter(isLive).length;

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Pianificazione Didattica</div>
          <h1 className="text-3xl font-bold text-stone-900">Calendario Lezioni</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF5EB] border border-[#F5D9B8] text-[#C96F0E] text-sm font-semibold" data-testid="calendario-oggi-count">
              <CalIcon className="w-3.5 h-3.5"/> {lezioniOggi} oggi
            </span>
            {isManager && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${liveCount > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500"}`} data-testid="calendario-live-count">
                <span className={`w-2 h-2 rounded-full ${liveCount > 0 ? "bg-emerald-500 animate-pulse" : "bg-stone-400"}`}/> {liveCount} live ora
              </span>
            )}
            {isManager && cap?.disponibile && (() => {
              const pct = cap.pool_max ? Math.min(100, Math.round(cap.posti_usati / cap.pool_max * 100)) : 0;
              const warn = pct >= 90; const mid = pct >= 70;
              const barColor = warn ? "bg-red-500" : mid ? "bg-[#F7941E]" : "bg-emerald-500";
              const chip = warn ? "bg-red-50 border-red-200 text-red-700" : mid ? "bg-[#FFF5EB] border-[#F5D9B8] text-[#C96F0E]" : "bg-emerald-50 border-emerald-200 text-emerald-700";
              return (
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border ${chip}`} data-testid="calendario-capienza" title={`${cap.lezioni_attive} lezioni attive · ${cap.partecipanti_attuali} partecipanti collegati · ${cap.posti_liberi} posti liberi`}>
                  <Users className="w-3.5 h-3.5"/>
                  {cap.posti_usati} / {cap.pool_max} posti piano
                  <span className="w-16 h-1.5 rounded-full bg-black/10 overflow-hidden">
                    <span className={`block h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}/>
                  </span>
                </span>
              );
            })()}
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-lezione-btn"><Plus className="w-4 h-4 mr-2"/>Nuova Lezione</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Programma lezione online</DialogTitle><DialogDescription>Lezione FAD sincrona: la stanza BigBlueButton viene generata automaticamente.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Classe</Label>
                  <Select value={form.classe_id} onValueChange={v=>setForm(f=>({...f, classe_id:v, materia:"", docente_id:""}))}>
                    <SelectTrigger data-testid="lezione-classe-select"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
                    <SelectContent>{classi.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {canBlended && (
                  <div className="col-span-2"><Label>Modalità</Label>
                    <Select value={form.modalita} onValueChange={v=>setForm({...form,modalita:v})}>
                      <SelectTrigger data-testid="lezione-modalita-select"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fad_sincrona">FAD Sincrona (con tracciamento presenze)</SelectItem>
                        <SelectItem value="blended">Blended (stanza BBB, senza presenze e orario)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isDocente && (
                  <div className="col-span-2"><Label>Docente</Label>
                    <Select value={form.docente_id} onValueChange={v=>setForm({...form,docente_id:v, materia:""})} disabled={!form.classe_id}>
                      <SelectTrigger data-testid="lezione-docente-select"><SelectValue placeholder="Docente della classe"/></SelectTrigger>
                      <SelectContent>{(classeSel?.docenti_dettagli||[]).map(d=><SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2"><Label>Materia</Label>
                  <Select value={form.materia} onValueChange={v=>setForm({...form,materia:v})}>
                    <SelectTrigger data-testid="lezione-materia-select"><SelectValue placeholder={materieOpzioni.length ? "Seleziona materia" : "Nessuna materia disponibile"}/></SelectTrigger>
                    <SelectContent>{materieOpzioni.map(m=><SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                  </Select>
                  {isDocente && materieOpzioni.length===0 && <div className="text-xs text-[#C53030] mt-1">Nessuna materia associata al tuo profilo.</div>}
                </div>
                <div><Label>Data</Label><Input type="date" min="2000-01-01" max="2100-12-31" data-testid="lezione-data-input" value={form.data} onChange={e=>setForm({...form,data:e.target.value})}/></div>
                <div><Label>Ore da svolgere</Label><Input type="number" step="0.5" data-testid="lezione-ore-input" value={form.ore_lezione} onChange={e=>setForm({...form,ore_lezione:e.target.value})} placeholder="0 = calcolate da orario"/></div>
                <div><Label>Max partecipanti aula (opzionale)</Label><Input type="number" min="1" data-testid="lezione-max-partecipanti-input" value={form.max_partecipanti} onChange={e=>setForm({...form,max_partecipanti:e.target.value})} placeholder="Vuoto = limite globale Impostazioni"/></div>
                {form.modalita !== "blended" && (<>
                  <div><Label>Ora inizio</Label><Input type="time" data-testid="lezione-ora-inizio" value={form.ora_inizio} onChange={e=>setForm({...form,ora_inizio:e.target.value})}/></div>
                  <div><Label>Ora fine</Label><Input type="time" data-testid="lezione-ora-fine" value={form.ora_fine} onChange={e=>setForm({...form,ora_fine:e.target.value})}/></div>
                </>)}
                <div className="col-span-2"><div className="border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2 text-sm text-emerald-800" data-testid="lezione-modalita-fad">{form.modalita === "blended" ? "Blended — stanza BBB creata, nessun tracciamento presenze, orario facoltativo" : "FAD Sincrona — link attivo 15 min prima dell'orario"}</div></div>
              </div>
              <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="lezione-save-btn">{saving ? "Salvataggio..." : "Salva"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="min-w-[160px]">
          <Input type="date" value={filtroData} onChange={e=>setFiltroData(e.target.value)} data-testid="filtro-data" title="Filtra per data"/>
        </div>
        <div className="min-w-[220px]">
          <Select value={filtroClasse} onValueChange={setFiltroClasse}>
            <SelectTrigger data-testid="filtro-classe"><SelectValue placeholder="Classe"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le classi</SelectItem>
              {classi.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <Select value={filtroDocente} onValueChange={setFiltroDocente}>
            <SelectTrigger data-testid="filtro-docente"><SelectValue placeholder="Docente"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i docenti</SelectItem>
              {docentiDisponibili.map(d=><SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <Select value={filtroMateria} onValueChange={setFiltroMateria}>
            <SelectTrigger data-testid="filtro-materia"><SelectValue placeholder="Materia"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le materie</SelectItem>
              {materieDisponibili.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-6">
        {dates.map(d => (
          <div key={d}>
            <div className="flex items-center gap-3 mb-3">
              <CalIcon className="w-4 h-4 text-[#F7941E]"/>
              <div className="text-sm font-semibold text-stone-900">{new Date(d).toLocaleDateString("it-IT", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {grouped[d].map(l => {
                const cl = classi.find(x=>x.id===l.classe_id);
                const docNome = cl?.docenti_dettagli?.find(x=>x.id===l.docente_id)?.nome;
                return (
                  <Card key={l.id} className="p-4 bg-white border border-[#E2E8F0] rounded-xl hover:border-[#F5D9B8] hover:shadow-md transition-all" data-testid={`lezione-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-900 text-base leading-snug break-words" data-testid={`lezione-titolo-${l.id}`}>{l.titolo}</div>
                        <div className="inline-flex items-center gap-1.5 mt-1 text-sm font-semibold text-[#C96F0E]"><Users className="w-3.5 h-3.5 shrink-0"/>{cl?.nome || "—"}</div>
                      </div>
                      {(user?.role === "admin" || user?.role === "collaboratore") && <Button variant="ghost" size="sm" className="shrink-0 h-8 w-8 p-0" onClick={()=>del(l.id)} data-testid={`del-lezione-${l.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-stone-700"><Clock className="w-3 h-3 text-stone-400"/>{l.modalita === "blended" ? "Blended" : `${l.ora_inizio} - ${l.ora_fine}`}</span>
                      {l.modalita !== "blended" && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-stone-700"><Video className="w-3 h-3 text-stone-400"/>FAD Sincrona</span>}
                      {l.materia && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-stone-700"><BookOpen className="w-3 h-3 text-stone-400"/>{l.materia}</span>}
                      {l.ore_lezione ? <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-stone-700">{l.ore_lezione}h</span> : null}
                      {docNome && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-medium text-stone-700"><User className="w-3 h-3 text-stone-400"/>{docNome}</span>}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {(user?.role === "admin" || user?.role === "collaboratore" || user?.role === "docente") && (
                        <button onClick={()=>openMaxEdit(l)} className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#F7941E] transition-colors" data-testid={`max-part-${l.id}`}>
                          <Users className="w-3 h-3"/> Max aula: <span className="font-semibold">{l.max_partecipanti ? l.max_partecipanti : "predefinito"}</span> <Pencil className="w-3 h-3"/>
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button onClick={()=>openNotif(l)} className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-[#1F4E79] transition-colors" data-testid={`notif-inviate-${l.id}`}>
                          <Bell className="w-3 h-3"/> Notifiche inviate
                        </button>
                      )}
                      {isLiveWindow(l) && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold" data-testid={`live-badge-${l.id}`}>
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                          LIVE{typeof l.live_studenti === "number" ? ` · ${l.live_studenti} collegat${l.live_studenti === 1 ? "o" : "i"}` : ""}
                        </span>
                      )}
                    </div>

                    {(l.bbb_link || (isEnded(l) && rec[l.id])) && (
                      <div className="mt-3 pt-3 border-t border-[#F1F5F9] flex flex-wrap items-center gap-2">
                        {l.bbb_link && (
                          <Button size="sm" onClick={()=>apriLezione(l)} className={`h-8 gap-1.5 text-xs font-semibold ${isLiveWindow(l) ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#F7941E] hover:bg-[#E08316] text-white"}`} data-testid={`entra-${l.id}`}>
                            <Video className="w-3.5 h-3.5"/> {isLiveWindow(l) ? "Entra nella lezione LIVE" : "Vai alla lezione"}
                          </Button>
                        )}
                        {isEnded(l) && rec[l.id] && (
                          <Button variant="outline" size="sm" onClick={()=>apriRegistrazione(l)} className="h-8 gap-1.5 text-xs font-semibold border-[#1F4E79]/40 text-[#1F4E79] hover:bg-[#1F4E79] hover:text-white" title="Disponibile al termine della lezione, dopo che BigBlueButton ha elaborato la registrazione (di solito pochi minuti)." data-testid={`registrazione-${l.id}`}>
                            <PlayCircle className="w-4 h-4"/> Guarda registrazione
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
        {dates.length === 0 && <div className="text-stone-500 text-center py-12">Nessuna lezione programmata.</div>}
      </div>

      <Dialog open={!!maxEdit} onOpenChange={(o)=>{ if(!o) setMaxEdit(null); }}>
        <DialogContent data-testid="max-part-dialog">
          <DialogHeader>
            <DialogTitle>Max partecipanti aula</DialogTitle>
            <DialogDescription>Modifica il limite per questa lezione senza ricrearla. Lascia vuoto per usare il limite globale delle Impostazioni. La modifica ha effetto dalla prossima apertura della stanza.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Numero massimo partecipanti</Label>
            <Input type="number" min="1" value={maxVal} onChange={e=>setMaxVal(e.target.value)} placeholder="Vuoto = limite globale Impostazioni" data-testid="max-part-input"/>
          </div>
          <DialogFooter><Button onClick={saveMax} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="max-part-save-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notifLez} onOpenChange={(o)=>{ if(!o){ setNotifLez(null); setNotifRows([]); } }}>
        <DialogContent data-testid="notif-inviate-dialog">
          <DialogHeader>
            <DialogTitle>Notifiche inviate</DialogTitle>
            <DialogDescription>Riepilogo degli avvisi inviati per la lezione <b>{notifLez?.titolo}</b> ({notifLez?.data}). Visibile solo all'amministratore.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {notifLoading && <div className="text-sm text-stone-500 py-6 text-center">Caricamento…</div>}
            {!notifLoading && notifRows.length === 0 && <div className="text-sm text-stone-500 py-6 text-center" data-testid="notif-inviate-empty">Nessuna notifica inviata per questa lezione.</div>}
            {!notifLoading && notifRows.map((n, i) => {
              const tipoLabel = { creazione: "Creazione", modifica: "Modifica", promemoria: "Promemoria" }[n.tipo] || n.tipo;
              const tipoColor = { creazione: "bg-emerald-50 text-emerald-700 border-emerald-200", modifica: "bg-[#FFF5EB] text-[#C96F0E] border-[#F5D9B8]", promemoria: "bg-blue-50 text-blue-700 border-blue-200" }[n.tipo] || "bg-stone-100 text-stone-600 border-stone-200";
              return (
                <div key={n.id || i} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-[#E2E8F0] bg-white" data-testid={`notif-row-${i}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-stone-900 truncate">{n.nome || "—"} <span className="text-xs font-normal text-stone-400">· {n.ruolo}</span></div>
                    <div className="text-xs text-stone-500">{(n.canali || []).join(" · ") || "—"} · {n.ts ? new Date(n.ts).toLocaleString("it-IT", { timeZone: "Europe/Rome", day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }) : ""}</div>
                  </div>
                  <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border ${tipoColor}`}>{tipoLabel}</span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
